import { Phone, Mail, MapPin } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { EstimateForm } from "@/components/EstimateForm";
import { BUSINESS, CITIES } from "@/siteData";
import { trackEvent } from "@/lib/analytics";

const Contact = () => (
  <>
    <Seo
      title="Get a Free Estimate | JC Colors Remodeling LLC"
      description="Request a free painting or drywall estimate in Overland Park, Kansas City, Lenexa or Olathe. Call 913-244-4798 or send a message."
    />

    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12" data-testid="contact-page">
      <Reveal>
        <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-amber-700">
          Contact
        </p>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl">
          Let's talk about your project
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
          Tell us what you're planning — a room refresh, a full exterior, a
          wall that needs repair — and we'll follow up to schedule your free
          estimate.
        </p>
      </Reveal>

      <div className="mt-12 grid gap-10 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
            <EstimateForm />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="space-y-4">
            <a
              href={BUSINESS.phoneHref}
              data-testid="contact-call-phone-card"
              onClick={() => trackEvent("phone_click", { location: "contact-page" })}
              className="group flex items-start gap-4 rounded-3xl bg-slate-900 p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-600">
                <Phone className="h-5 w-5 text-white" />
              </span>
              <span>
                <span className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Call us directly
                </span>
                <span className="mt-1 block font-display text-xl font-bold text-white group-hover:text-amber-400">
                  {BUSINESS.phone}
                </span>
                <span className="mt-1 block text-sm text-slate-400">
                  Tap to call — fastest way to reach us
                </span>
              </span>
            </a>
            <a
              href={BUSINESS.emailHref}
              data-testid="contact-email-card"
              className="group flex items-start gap-4 rounded-3xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-amber-600/50 hover:shadow-lg"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-600/10">
                <Mail className="h-5 w-5 text-amber-700" />
              </span>
              <span>
                <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Email
                </span>
                <span className="mt-1 block font-display text-xl font-bold text-slate-900 group-hover:text-amber-700">
                  {BUSINESS.email}
                </span>
                <span className="mt-1 block text-sm text-slate-600">
                  Send photos of your project for a faster estimate
                </span>
              </span>
            </a>
            <div className="rounded-3xl border border-slate-200 bg-white p-6">
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-600/10">
                  <MapPin className="h-5 w-5 text-amber-700" />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Service area
                  </p>
                  <p className="mt-1 font-display text-lg font-bold text-slate-900">
                    Kansas City metro
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-slate-600">
                    {CITIES.map((c) => (
                      <li key={c.slug}>
                        {c.city}, {c.state}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Contact;
